import React from 'react';
import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';

const PricingTier = ({ name, price, period, description, features, highlighted = false, badge = null, cta }) => (
  <div className={`apple-panel p-5 sm:p-6 rounded-apple relative flex flex-col ${highlighted ? 'ring-2 ring-sortio-primary' : ''}`}>
    {badge && (
      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 px-4 py-1 rounded-full bg-sortio-primary/80 backdrop-blur-md border border-white/20 shadow-lg">
        <span className="text-xs font-medium text-white">{badge}</span>
      </div>
    )}

    <div className={`flex flex-col flex-grow ${badge ? 'pt-4' : ''}`}>
      <h3 className="text-lg font-semibold text-apple-50 mb-1">{name}</h3>
      <div className="flex items-baseline mb-1">
        <span className="text-apple-300 text-base mr-1">$</span>
        <span className="text-3xl font-bold text-apple-50">{price}</span>
        {period && <span className="text-apple-400 ml-1 text-xs">{period}</span>}
      </div>
      <p className="text-apple-400 text-xs mb-4">{description}</p>

      <ul className="list-none space-y-2 mb-4 flex-grow">
        {features.map((item, index) => (
          <li key={index} className="flex items-start">
            <div className="flex-shrink-0 w-4 h-4 rounded-full bg-sortio-primary/20 flex items-center justify-center mr-2 mt-0.5">
              <Check className="w-2.5 h-2.5 text-sortio-primary" />
            </div>
            <span className="text-apple-200 text-xs leading-relaxed">{item}</span>
          </li>
        ))}
      </ul>

      {cta && <div className="mt-auto">{cta}</div>}
    </div>
  </div>
);

const Pricing = () => {
  return (
    <section id="pricing" className="my-20">
      <div className="text-center mb-12">
        <h2 className="text-3xl sm:text-4xl font-semibold mb-4 tracking-tight text-apple-50">
          Start free. Upgrade only if you need to.
        </h2>
        <p className="text-apple-300 text-lg font-light max-w-2xl mx-auto">
          Most households are well-served by the Free tier. Upgrade to Pro when you're ready to
          tackle thousands of files in one go, or set up auto-sort on your Downloads folder.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
        <PricingTier
          name="Free"
          price="0"
          period="forever"
          description="Often the right fit for personal use"
          badge="Start Here"
          features={[
            'One-time AI trial allowance, enough to try Sortio on a real folder',
            'Rule-based sorting: free and unlimited',
            'Preview before applying',
            'Sort history and undo',
            'Local LLM or BYOK supported',
            'iCloud, Google Drive, Dropbox folders',
          ]}
          cta={
            <Link to="/download" className="block">
              <button className="w-full py-2.5 rounded-lg text-sm font-medium text-white bg-sortio-primary hover:bg-sortio-primary/90 transition-all shadow-lg shadow-sortio-primary/25">
                Download Free
              </button>
            </Link>
          }
        />

        <PricingTier
          name="Pro"
          price="14.99"
          period="/mo or $99/yr"
          description="For the 5,000-file Downloads folder"
          highlighted={true}
          features={[
            'Monthly AI allowance sized for heavy use',
            'AI sort up to 5,000 files per run',
            'Auto-sort on file change',
            'Rule Builder for repeatable workflows',
            'Spaces for separate archives',
            'Unlimited file renaming',
            'Bring your own AI key: unmetered, no allowance used',
            'Usage shown as a simple percentage in Settings',
            'Email support',
          ]}
          cta={
            <Link to="/download" className="block">
              <button className="w-full py-2.5 rounded-lg text-sm font-medium text-apple-200 bg-apple-600/30 border border-apple-500/30 hover:bg-apple-600/50 transition-all">
                Try Pro
              </button>
            </Link>
          }
        />

        <PricingTier
          name="Family"
          price="0"
          period=""
          description="Use Sortio on every Mac in the house"
          features={[
            'Free tier on every device',
            'Each person keeps their own archive',
            'Nothing syncs between machines',
            'Pro upgrade is per-Mac, not per-person',
            'No accounts needed for Free use',
          ]}
          cta={
            <Link to="/download" className="block">
              <button className="w-full py-2.5 rounded-lg text-sm font-medium text-apple-200 bg-apple-600/30 border border-apple-500/30 hover:bg-apple-600/50 transition-all">
                Download for Each Mac
              </button>
            </Link>
          }
        />
      </div>

      <p className="text-center text-apple-400 text-xs mt-8 max-w-2xl mx-auto">
        Not sure which tier? Start on Free. If you ever use up the one-time AI trial allowance on
        a real cleanup job, upgrade then. Many of our home users never need to.
      </p>
    </section>
  );
};

export default Pricing;
